import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AlertTriangle, ArrowUp, Brain, Check, ChevronDown, ChevronRight, Copy, Database, Loader2, Paperclip, Plus, Share2, Square, Wrench, X } from 'lucide-react'
import { Spinner } from '../../components/ui'
import { useChatStore, type ChatMessage, type AiBlock } from '../../stores/chatStore'
import { toolLabel } from './toolActivity'
import {
  fetchMessages, mapApiMessageToStore, createConversation,
  fetchConversationCharts, fetchConversationResults, fetchConversationSpreadsheets,
  fetchConvDataSources, attachConvDataSource, detachConvDataSource,
  type ApiConvChart, type ApiConvResult, type ApiConvSpreadsheet,
} from '../../lib/chatApi'
import { fetchDataSources, type ApiDataSource } from '../../lib/api'
import type { ChartPayload } from './ChartEmbed'
import { ResultTile } from './ResultTile'
import type { SpreadsheetPayload } from './SpreadsheetEmbed'
import { SpreadsheetCard } from './SpreadsheetCard'
import { SpreadsheetSidePanel } from './SpreadsheetSidePanel'
import { useAgent } from './useAgent'
import { useAuth } from '../../components/auth-context'
import { useActiveWorkspace } from '../../lib/workspace'

const ChartEmbed = lazy(() => import('./ChartEmbed'))

function timeOfDayGreeting(date: Date = new Date()): string {
  const h = date.getHours()
  if (h >= 5 && h < 12) return 'Good morning'
  if (h >= 12 && h < 18) return 'Good afternoon'
  return 'Good evening'
}

function chartPayload(c: ApiConvChart): ChartPayload {
  return {
    id: c.id, title: c.title,
    spec: c.spec as Record<string, unknown>,
    data: c.data as ChartPayload['data'],
    artifactId: c.artifact_id, versionNum: c.version_num,
    createdAt: c.created_at, savedByUser: c.saved_by_user, messageId: c.message_id,
  }
}
function spreadsheetPayload(s: ApiConvSpreadsheet): SpreadsheetPayload {
  return {
    id: s.id, title: s.title, artifactId: s.artifact_id, versionNum: s.version_num,
    createdAt: s.created_at, savedByUser: s.saved_by_user, messageId: s.message_id,
  }
}

interface Artifacts {
  charts: ChartPayload[]
  results: ApiConvResult[]
  spreadsheets: SpreadsheetPayload[]
}

export type ImageAttachment = {
  mimeType: string
  dataBase64: string
  previewUrl: string
  name: string
}

const MAX_ATTACHMENTS = 4
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024
const ALLOWED_MIMES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])
const EMPTY_SOURCE_NAMES: string[] = []
const EMPTY_MESSAGES: ChatMessage[] = []

function DataSourcesStrip({
  active,
  available,
  expanded,
  setExpanded,
  onAdd,
  onRemove,
}: {
  active: ApiDataSource[]
  available: ApiDataSource[]
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  onAdd: (name: string) => void
  onRemove: (name: string) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <div className="mx-2 rounded-t-xl bg-[#e8e8e8] px-3 py-2 sm:mx-4 sm:px-4">
      <div className="flex w-full items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-[13px] font-medium text-gray-600"
          >
            <ChevronDown
              size={13}
              className={`mr-1 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`}
            />
            Data Sources ({active.length})
          </button>
          {expanded && (
            <div className="mt-1.5 flex flex-wrap gap-2">
              {active.map((source) => (
                <span key={source.name} className="inline-flex items-center text-[13px] text-gray-600">
                  <span className="mr-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-slate-700">
                    <Database size={9} className="text-white" />
                  </span>
                  {source.name}
                  <button
                    type="button"
                    onClick={() => onRemove(source.name)}
                    className="ml-1.5 text-gray-400 transition-colors hover:text-gray-600"
                    aria-label={`Remove ${source.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {active.length === 0 && (
                <span className="text-xs text-gray-500">No sources attached</span>
              )}
            </div>
          )}
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
            title="Add data source"
            aria-label="Add data source"
          >
            <Plus size={15} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute bottom-6 right-0 z-20 max-h-72 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {available.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">All sources attached</div>
                ) : (
                  available.map((source) => (
                    <button
                      key={source.name}
                      type="button"
                      onClick={() => {
                        onAdd(source.name)
                        setMenuOpen(false)
                        setExpanded(true)
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-slate-700">
                        <Database size={9} className="text-white" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate">{source.name}</span>
                        <span className="block truncate font-mono text-[11px] text-gray-400">{source.table}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function ConversationView({ conversationId }: { conversationId: string | null }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false }) as { sheet?: string; v?: number }
  const session = useAuth()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const setMessages = useChatStore((s) => s.setMessages)
  const setActiveConversation = useChatStore((s) => s.setActiveConversation)
  const setPendingFirstMessage = useChatStore((s) => s.setPendingFirstMessage)
  const messages = useChatStore((s) => (conversationId ? s.messagesById[conversationId] : undefined)) ?? EMPTY_MESSAGES
  const [sourcesExpanded, setSourcesExpanded] = useState(false)
  const [agentName, setAgentName] = useState('assistant-v1')
  const [pendingAddSourceNames, setPendingAddSourceNames] = useState<string[]>([])
  const [pendingRemoveSourceNames, setPendingRemoveSourceNames] = useState<string[]>([])
  const [shareCopied, setShareCopied] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['messages', activeWorkspaceKey, conversationId],
    queryFn: () => fetchMessages(activeWorkspaceKey, conversationId as string),
    enabled: !!activeWorkspaceKey && !!conversationId,
  })
  const { data: charts } = useQuery({
    queryKey: ['conversationCharts', activeWorkspaceKey, conversationId],
    queryFn: () => fetchConversationCharts(activeWorkspaceKey, conversationId as string),
    enabled: !!activeWorkspaceKey && !!conversationId,
  })
  const { data: results } = useQuery({
    queryKey: ['conversationResults', activeWorkspaceKey, conversationId],
    queryFn: () => fetchConversationResults(activeWorkspaceKey, conversationId as string),
    enabled: !!activeWorkspaceKey && !!conversationId,
  })
  const { data: spreadsheets } = useQuery({
    queryKey: ['conversationSpreadsheets', activeWorkspaceKey, conversationId],
    queryFn: () => fetchConversationSpreadsheets(activeWorkspaceKey, conversationId as string),
    enabled: !!activeWorkspaceKey && !!conversationId,
  })
  const { data: allSources } = useQuery({
    queryKey: ['data-sources'],
    queryFn: fetchDataSources,
  })
  const { data: convSourceNames } = useQuery({
    queryKey: ['conversationDataSources', activeWorkspaceKey, conversationId],
    queryFn: () => fetchConvDataSources(activeWorkspaceKey, conversationId as string),
    enabled: !!activeWorkspaceKey && !!conversationId,
  })

  useEffect(() => {
    if (loaded && conversationId) setMessages(conversationId, loaded.map(mapApiMessageToStore))
  }, [loaded, conversationId, setMessages])

  useEffect(() => {
    setActiveConversation(conversationId)
    return () => setActiveConversation(null)
  }, [conversationId, setActiveConversation])

  const artifacts = useMemo<Artifacts>(() => {
    const chartRows = charts ?? []
    const sheetRows = spreadsheets ?? []

    return {
      charts: chartRows.map(chartPayload),
      results: results ?? [],
      spreadsheets: sheetRows.map(spreadsheetPayload),
    }
  }, [charts, results, spreadsheets])

  const { sendMessage, stopGeneration, connectionStatus, isStreaming } = useAgent({ conversationId, workspaceKey: activeWorkspaceKey })
  const defaultSourceNames = useMemo(
    () => (allSources ?? []).map((source) => source.name),
    [allSources],
  )
  const defaultSourceNameSet = useMemo(
    () => new Set(defaultSourceNames),
    [defaultSourceNames],
  )
  const activeSourceNames = useMemo(
    () => {
      if (conversationId) return convSourceNames ?? EMPTY_SOURCE_NAMES
      const names = new Set(defaultSourceNames)
      for (const name of pendingRemoveSourceNames) names.delete(name)
      for (const name of pendingAddSourceNames) names.add(name)
      return [...names]
    },
    [conversationId, convSourceNames, defaultSourceNames, pendingAddSourceNames, pendingRemoveSourceNames],
  )
  const activeSources = useMemo(() => {
    const names = new Set(activeSourceNames)
    return (allSources ?? []).filter((source) => names.has(source.name))
  }, [allSources, activeSourceNames])
  const availableSources = useMemo(() => {
    const names = new Set(activeSourceNames)
    return (allSources ?? []).filter((source) => !names.has(source.name))
  }, [allSources, activeSourceNames])

  // Pin a just-sent user prompt near the top so the streaming answer grows
  // below it. Conversation switches and first async message loads land at
  // the latest message, while token appends do not yank the viewport.
  const prevConvIdRef = useRef<string | null>(null)
  const prevMsgCountRef = useRef(0)
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const prevConvId = prevConvIdRef.current
    const prevCount = prevMsgCountRef.current
    const isConvSwitch = conversationId !== prevConvId
    prevConvIdRef.current = conversationId
    prevMsgCountRef.current = messages.length

    if (messages.length === 0) return

    const last = messages[messages.length - 1]
    const grew = messages.length > prevCount

    const scrollLastIntoView = () => {
      requestAnimationFrame(() => {
        const el = container.querySelector(`[data-message-id="${last.id}"]`) as HTMLElement | null
        if (!el) return
        const offset = el.getBoundingClientRect().bottom - container.getBoundingClientRect().bottom + container.scrollTop
        container.scrollTo({ top: Math.max(0, offset) })
      })
    }

    if (isConvSwitch) {
      scrollLastIntoView()
      return
    }

    if (prevCount === 0 && last.role === 'ai') {
      scrollLastIntoView()
      return
    }

    if (grew && last.role === 'user') {
      requestAnimationFrame(() => {
        const el = container.querySelector(`[data-message-id="${last.id}"]`) as HTMLElement | null
        if (!el) return
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
        container.scrollTo({ top: top - 24, behavior: 'smooth' })
      })
    }
  }, [messages, conversationId])

  const addSource = (name: string) => {
    if (!conversationId) {
      if (defaultSourceNameSet.has(name)) {
        setPendingRemoveSourceNames((prev) => prev.filter((source) => source !== name))
        return
      }
      setPendingAddSourceNames((prev) => prev.includes(name) ? prev : [...prev, name])
      return
    }
    void attachConvDataSource(activeWorkspaceKey, conversationId, name).then(() => {
      queryClient.invalidateQueries({ queryKey: ['conversationDataSources', activeWorkspaceKey, conversationId] })
    })
  }

  const removeSource = (name: string) => {
    if (!conversationId) {
      setPendingAddSourceNames((prev) => prev.filter((source) => source !== name))
      if (defaultSourceNameSet.has(name)) {
        setPendingRemoveSourceNames((prev) => prev.includes(name) ? prev : [...prev, name])
      }
      return
    }
    void detachConvDataSource(activeWorkspaceKey, conversationId, name).then(() => {
      queryClient.invalidateQueries({ queryKey: ['conversationDataSources', activeWorkspaceKey, conversationId] })
    })
  }

  const handleSend = async (text: string, attachments: ImageAttachment[] = []) => {
    const wireAttachments = attachments.map((a) => ({
      mime_type: a.mimeType,
      data_base64: a.dataBase64,
    }))
    if (!activeWorkspaceKey) {
      alert('Pick a workspace first.')
      return
    }
    if (conversationId) {
      sendMessage(text, wireAttachments.length > 0 ? wireAttachments : undefined)
      return
    }
    const { id } = await createConversation(activeWorkspaceKey, { agent_name: agentName })
    if (pendingAddSourceNames.length > 0 || pendingRemoveSourceNames.length > 0) {
      await Promise.allSettled([
        ...pendingAddSourceNames.map((source) => attachConvDataSource(activeWorkspaceKey, id, source)),
        ...pendingRemoveSourceNames.map((source) => detachConvDataSource(activeWorkspaceKey, id, source)),
      ])
      setPendingAddSourceNames([])
      setPendingRemoveSourceNames([])
      queryClient.invalidateQueries({ queryKey: ['conversationDataSources', activeWorkspaceKey, id] })
    }
    setPendingFirstMessage({
      conversationId: id,
      text,
      attachments: wireAttachments.length > 0 ? wireAttachments : undefined,
    })
    queryClient.invalidateQueries({ queryKey: ['conversations', activeWorkspaceKey] })
    navigate({ to: '/assistant/$conversationId', params: { conversationId: id } })
  }

  const firstName = session.user?.firstName?.trim()
  const greeting = firstName ? `${timeOfDayGreeting()}, ${firstName}` : timeOfDayGreeting()
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } catch {
      // best-effort
    }
  }

  const chat = !activeWorkspaceKey ? (
    <div className="flex h-full min-w-0 flex-1 items-center justify-center bg-app-bg px-6 text-center text-sm text-gray-500">
      Pick a workspace to start chatting.
    </div>
  ) : !conversationId ? (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <div className="h-14 shrink-0" />
      <div className="flex flex-1 items-center justify-center px-4 sm:px-6 lg:px-12">
        <div className="w-full max-w-3xl">
          <h1 className="mb-2 text-center text-3xl font-medium text-gray-400 md:text-4xl">
            {greeting}
          </h1>
          <p className="mb-8 text-center text-sm text-gray-400">
            Attach data sources from the panel below, then tell me what you want to analyze.
          </p>
          <DataSourcesStrip
            active={activeSources}
            available={availableSources}
            expanded={sourcesExpanded}
            setExpanded={setSourcesExpanded}
            onAdd={addSource}
            onRemove={removeSource}
          />
          <div className="mb-2 flex items-center justify-end gap-2">
            <label htmlFor="assistant-agent" className="text-xs text-gray-400">
              Assistant
            </label>
            <select
              id="assistant-agent"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="rounded-md border border-gray-300 bg-app-bg px-2 py-1 text-xs text-gray-600"
            >
              <option value="assistant-v1">Assistant v1</option>
              <option value="assistant-v2">Assistant v2</option>
            </select>
          </div>
          <Composer onSend={handleSend} onStop={stopGeneration} isStreaming={isStreaming} />
        </div>
      </div>
    </div>
  ) : (
    <div className="relative flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <div className="z-10 flex h-14 shrink-0 items-center justify-end gap-3 px-4 md:px-6">
        {messages.length > 0 && !isStreaming && (
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-200/50 hover:text-gray-700"
            title="Copy conversation link"
          >
            {shareCopied ? <Check size={14} className="text-green-500" /> : <Share2 size={14} />}
            <span>{shareCopied ? 'Copied!' : 'Share'}</span>
          </button>
        )}
        <StatusDot status={connectionStatus} />
      </div>

      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto px-3 sm:px-6 lg:px-12 ${messages.length === 0 ? 'pb-48 md:pb-56' : 'pb-[80vh]'}`}
      >
        <div className="mx-auto w-full max-w-4xl pt-6">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : messages.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400">
              <p className="text-lg font-medium">Start a conversation</p>
              <p className="mt-1 text-sm">Attach data sources from the panel below, then tell me what you want to analyze.</p>
            </div>
          ) : (
            messages.map((m, i) => (
              <MessageView
                key={m.id}
                msg={m}
                artifacts={artifacts}
                previousMessageAt={i > 0 ? messages[i - 1].timestamp : 0}
                isStreaming={isStreaming && i === messages.length - 1 && m.role === 'ai'}
              />
            ))
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-app-bg via-app-bg to-transparent px-3 pb-4 pt-6 sm:px-6 md:pb-8 md:pt-10 lg:px-12">
        <div className="mx-auto w-full max-w-3xl">
          <DataSourcesStrip
            active={activeSources}
            available={availableSources}
            expanded={sourcesExpanded}
            setExpanded={setSourcesExpanded}
            onAdd={addSource}
            onRemove={removeSource}
          />
          <Composer onSend={handleSend} onStop={stopGeneration} isStreaming={isStreaming} />
        </div>
      </div>
    </div>
  )

  if (search.sheet && conversationId) {
    return (
      <div className="flex flex-1 min-h-0">
        <div className="flex flex-1 min-w-0 flex-col">{chat}</div>
        <div className="w-[640px] shrink-0 border-l border-gray-200">
          <SpreadsheetSidePanel
            key={`${search.sheet}:${search.v ?? 'latest'}`}
            conversationId={conversationId}
            artifactId={search.sheet}
            versionNum={search.v ?? null}
            onVersionChange={(v) =>
              navigate({ to: '/assistant/$conversationId', params: { conversationId }, search: (p) => ({ ...p, v }) })}
            onClose={() =>
              navigate({ to: '/assistant/$conversationId', params: { conversationId }, search: {} })}
          />
        </div>
      </div>
    )
  }
  return chat
}

const MARKER_RE = /<!--\s*(chart|result|spreadsheet|chart_failed|result_failed|spreadsheet_failed):([^\s]+?)\s*-->/g
const TRAILING_TABLE_RE = /(?:^|\n)[ \t]*\|[^\n]*\|[ \t]*\n[ \t]*\|(?:[-: \t]+\|)+[ \t]*(?:\n[ \t]*\|[^\n]+)*\s*$/

function stripTrailingTable(text: string): string {
  return text.replace(TRAILING_TABLE_RE, '\n')
}

type Segment =
  | { type: 'md'; text: string }
  | { type: 'chart'; id: string }
  | { type: 'result'; id: string }
  | { type: 'spreadsheet'; id: string }
  | { type: 'failed'; kind: 'chart' | 'result' | 'spreadsheet'; id: string }

function splitOnMarkers(text: string): Segment[] {
  const parts: Segment[] = []
  const re = new RegExp(MARKER_RE.source, 'g')
  let last = 0
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      let md = text.slice(last, match.index)
      if (match[1] === 'result' || match[1] === 'result_failed') {
        md = stripTrailingTable(md)
      }
      parts.push({ type: 'md', text: md })
    }
    const kind = match[1]
    const id = match[2]
    if (kind === 'chart') parts.push({ type: 'chart', id })
    else if (kind === 'result') parts.push({ type: 'result', id })
    else if (kind === 'spreadsheet') parts.push({ type: 'spreadsheet', id })
    else {
      parts.push({
        type: 'failed',
        kind: kind === 'chart_failed' ? 'chart' : kind === 'spreadsheet_failed' ? 'spreadsheet' : 'result',
        id,
      })
    }
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push({ type: 'md', text: text.slice(last) })
  return parts
}

type RenderedSets = {
  charts: Set<string>
  results: Set<string>
  spreadsheets: Set<string>
}

type ToolStep = {
  name: string
  input?: Record<string, unknown> | null
  result?: string
  isError?: boolean
  done: boolean
}

type RenderItem =
  | { kind: 'text'; block: AiBlock & { type: 'text' } }
  | { kind: 'thinking-group'; texts: string[] }
  | { kind: 'tool-group'; steps: ToolStep[] }

function groupAiBlocks(blocks: AiBlock[]): RenderItem[] {
  const items: RenderItem[] = []
  let openThinking: { kind: 'thinking-group'; texts: string[] } | null = null
  let openTools: { kind: 'tool-group'; steps: ToolStep[] } | null = null
  const flushThinking = () => {
    if (openThinking) { items.push(openThinking); openThinking = null }
  }
  const flushTools = () => {
    if (openTools) { items.push(openTools); openTools = null }
  }
  for (const block of blocks) {
    switch (block.type) {
      case 'thinking':
        flushTools()
        ;(openThinking ??= { kind: 'thinking-group', texts: [] }).texts.push(block.text)
        break
      case 'text':
        flushThinking()
        flushTools()
        items.push({ kind: 'text', block })
        break
      case 'tool_call':
        flushThinking()
        ;(openTools ??= { kind: 'tool-group', steps: [] }).steps.push({
          name: block.name, input: block.input ?? null, done: false,
        })
        break
      case 'tool_result': {
        openTools ??= { kind: 'tool-group', steps: [] }
        const pending = [...openTools.steps].reverse().find((s) => !s.done)
        if (pending) {
          pending.result = block.content
          pending.isError = block.isError
          pending.done = true
        } else {
          openTools.steps.push({ name: 'result', result: block.content, isError: block.isError, done: true })
        }
        break
      }
    }
  }
  flushThinking()
  flushTools()
  return items
}

function splitTurnRenderItems(renderItems: RenderItem[], isStreaming: boolean): {
  leadAnswerItems: RenderItem[]
  processItems: RenderItem[]
  finalAnswerItems: RenderItem[]
  hasFinalAnswer: boolean
} {
  let leadEnd = 0
  while (leadEnd < renderItems.length && renderItems[leadEnd].kind === 'text') {
    leadEnd++
  }
  const leadAnswerItems = renderItems.slice(0, leadEnd)
  const rest = renderItems.slice(leadEnd)

  const firstFinalIdx = rest.findIndex(
    (it) => it.kind === 'text' && it.block.phase === 'final_answer',
  )
  if (firstFinalIdx >= 0) {
    return {
      leadAnswerItems,
      processItems: rest.slice(0, firstFinalIdx),
      finalAnswerItems: rest.slice(firstFinalIdx),
      hasFinalAnswer: true,
    }
  }

  if (!isStreaming) {
    if (rest.length === 0) {
      const answer = leadAnswerItems.length > 0 ? leadAnswerItems : renderItems
      return { leadAnswerItems: answer, processItems: [], finalAnswerItems: [], hasFinalAnswer: answer.length > 0 }
    }
    const hasProcessKinds = rest.some((it) => it.kind !== 'text')
    if (!hasProcessKinds) {
      return {
        leadAnswerItems: [...leadAnswerItems, ...rest],
        processItems: [],
        finalAnswerItems: [],
        hasFinalAnswer: true,
      }
    }
    let lastTextIdx = -1
    rest.forEach((it, idx) => {
      if (it.kind === 'text') lastTextIdx = idx
    })
    if (lastTextIdx >= 0) {
      return {
        leadAnswerItems,
        processItems: rest.slice(0, lastTextIdx),
        finalAnswerItems: rest.slice(lastTextIdx),
        hasFinalAnswer: true,
      }
    }
  }

  return { leadAnswerItems, processItems: rest, finalAnswerItems: [], hasFinalAnswer: false }
}

function FailureBadge({ children }: { children: string }) {
  return (
    <div className="mx-auto my-4 max-w-2xl">
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
        <AlertTriangle size={13} />
        {children}
      </span>
    </div>
  )
}

function ChartSkeleton() {
  return (
    <div className="my-4">
      <div className="mx-auto mb-2 h-4 w-40 animate-pulse rounded bg-gray-200" />
      <div className="mx-auto h-56 max-w-xl animate-pulse rounded bg-gray-100" />
    </div>
  )
}

function currentActivityLabel(blocks: AiBlock[]): string {
  for (let i = blocks.length - 1; i >= 0; i--) {
    const b = blocks[i]
    if (b.type === 'tool_call') return `${toolLabel(b.name, b.input)}…`
    if (b.type === 'tool_result') return 'Working…'
    if (b.type === 'thinking') return 'Thinking…'
    if (b.type === 'text') {
      if (b.phase === 'commentary') return 'Working…'
      return 'Writing…'
    }
  }
  return 'Thinking…'
}

function ActivityIndicator({ blocks }: { blocks: AiBlock[] }) {
  const [startedAt] = useState(() => Date.now())
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000))
  return (
    <div className="flex items-center gap-2 py-3 text-gray-500">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-500" />
      </span>
      <span className="text-sm font-medium">{currentActivityLabel(blocks)}</span>
      <span className="text-xs tabular-nums text-gray-400">{secs}s</span>
    </div>
  )
}

function fillSpreadsheetTitles(versions: SpreadsheetPayload[]): SpreadsheetPayload[] {
  const fallbackTitle = versions.find((v) => v.title.trim().length > 0)?.title ?? ''
  if (!fallbackTitle) return versions
  return versions.map((v) => (v.title.trim().length > 0 ? v : { ...v, title: fallbackTitle }))
}

// Markdown / chips / action icons clamp to the narrow column for readable
// line lengths; charts span the full wider chat column.
function StreamingText({ text }: { text: string }) {
  if (!text) return null
  return (
    <div className="mx-auto max-w-2xl whitespace-pre-wrap break-words text-[15px] leading-relaxed text-gray-900">
      {text}
      <span className="ml-px inline-block h-[1.05em] w-[2px] translate-y-[2px] animate-pulse bg-indigo-400" aria-hidden />
    </div>
  )
}

function Prose({ text, streaming }: { text: string; streaming?: boolean }) {
  if (!text.trim()) return null
  if (streaming) return <StreamingText text={text} />
  return (
    <div className="mx-auto max-w-2xl">
      <MarkdownContent content={text} />
    </div>
  )
}

function MessageView({
  msg,
  artifacts,
  previousMessageAt,
  isStreaming,
}: {
  msg: ChatMessage
  artifacts: Artifacts
  previousMessageAt: number
  isStreaming: boolean
}) {
  const [copied, setCopied] = useState(false)
  const failedArtifacts = useChatStore((s) => s.failedArtifacts)

  if (msg.role === 'user') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8 w-full"
        data-message-id={msg.id}
      >
        <div className="mx-auto flex w-full max-w-2xl justify-end">
          <div className="max-w-[80%] rounded-2xl bg-accent px-5 py-3.5 text-[15px] leading-relaxed text-accent-text">
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {msg.attachments.map((a, i) => {
                  const src = `data:${a.mimeType};base64,${a.dataBase64}`
                  return (
                    <a
                      key={i}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="block h-24 w-24 overflow-hidden rounded-md border border-gray-200"
                    >
                      <img src={src} alt="attachment" className="h-full w-full object-cover" />
                    </a>
                  )
                })}
              </div>
            )}
            <MarkdownContent content={msg.content} />
          </div>
        </div>
      </motion.div>
    )
  }

  const aiBlocks = msg.blocks ?? (msg.content ? [{ type: 'text' as const, text: msg.content }] : [])
  const hasContent = aiBlocks.length > 0
  const copyText = (() => {
    const textBlocks = aiBlocks.filter((b): b is AiBlock & { type: 'text' } => b.type === 'text')
    const finalBlocks = textBlocks.filter((b) => b.phase === 'final_answer')
    let leadEnd = 0
    while (leadEnd < aiBlocks.length && aiBlocks[leadEnd].type === 'text') leadEnd++
    const leadBlocks = aiBlocks.slice(0, leadEnd).filter((b): b is AiBlock & { type: 'text' } => b.type === 'text')
    if (finalBlocks.length > 0) {
      const parts = [...leadBlocks, ...finalBlocks].map((b) => b.text).filter(Boolean)
      if (parts.length > 0) return parts.join('\n\n')
    }
    const source = textBlocks.filter((b) => b.phase !== 'commentary')
    return source.map((b) => b.text).join('\n\n') || msg.content
  })()

  const resultById = new Map<string, ApiConvResult>()
  for (const result of artifacts.results) resultById.set(result.id, result)

  const chartByRowId = new Map<string, ChartPayload>()
  const chartVersionsByArtifact = new Map<string, ChartPayload[]>()
  for (const chart of artifacts.charts) {
    chartByRowId.set(chart.id, chart)
    const arr = chartVersionsByArtifact.get(chart.artifactId) ?? []
    arr.push(chart)
    chartVersionsByArtifact.set(chart.artifactId, arr)
  }
  for (const versions of chartVersionsByArtifact.values()) {
    versions.sort((a, b) => a.versionNum - b.versionNum)
  }

  const spreadsheetByRowId = new Map<string, SpreadsheetPayload>()
  const spreadsheetVersionsByArtifact = new Map<string, SpreadsheetPayload[]>()
  for (const sheet of artifacts.spreadsheets) {
    spreadsheetByRowId.set(sheet.id, sheet)
    const arr = spreadsheetVersionsByArtifact.get(sheet.artifactId) ?? []
    arr.push(sheet)
    spreadsheetVersionsByArtifact.set(sheet.artifactId, arr)
  }
  for (const [artifactId, versions] of spreadsheetVersionsByArtifact) {
    versions.sort((a, b) => a.versionNum - b.versionNum)
    spreadsheetVersionsByArtifact.set(artifactId, fillSpreadsheetTitles(versions))
  }

  const resolveChart = (markerId: string): { current: ChartPayload; versions: ChartPayload[] } | null => {
    let artifactId = markerId
    if (!chartVersionsByArtifact.has(artifactId)) {
      const row = chartByRowId.get(markerId)
      if (!row) return null
      artifactId = row.artifactId
    }
    const versions = chartVersionsByArtifact.get(artifactId) ?? []
    if (versions.length === 0) return null
    const sameTurn = msg.dbId ? versions.filter((v) => v.messageId === msg.dbId) : []
    if (sameTurn.length > 0) return { current: sameTurn[sameTurn.length - 1], versions }
    const eligible = versions.filter((v) => new Date(v.createdAt).getTime() <= msg.timestamp)
    return { current: eligible.length > 0 ? eligible[eligible.length - 1] : versions[0], versions }
  }

  const resolveSpreadsheet = (markerId: string): { current: SpreadsheetPayload; versions: SpreadsheetPayload[] } | null => {
    let artifactId = markerId
    if (!spreadsheetVersionsByArtifact.has(artifactId)) {
      const row = spreadsheetByRowId.get(markerId)
      if (!row) return null
      artifactId = row.artifactId
    }
    const versions = spreadsheetVersionsByArtifact.get(artifactId) ?? []
    if (versions.length === 0) return null
    const sameTurn = msg.dbId ? versions.filter((v) => v.messageId === msg.dbId) : []
    if (sameTurn.length > 0) return { current: sameTurn[sameTurn.length - 1], versions }
    const eligible = versions.filter((v) => new Date(v.createdAt).getTime() <= msg.timestamp)
    return { current: eligible.length > 0 ? eligible[eligible.length - 1] : versions[0], versions }
  }

  const rendered: RenderedSets = { charts: new Set(), results: new Set(), spreadsheets: new Set() }
  const belongsToMessage = (messageId: string | null, createdAt: string) => {
    if (messageId === msg.dbId) return true
    if (messageId != null) return false
    const created = new Date(createdAt).getTime()
    return created > previousMessageAt && created <= msg.timestamp
  }

  const handleCopy = () => {
    if (!copyText.trim()) return
    navigator.clipboard.writeText(copyText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const renderItems = groupAiBlocks(aiBlocks)
  const lastItemIndex = renderItems.length - 1

  const renderItem = (item: RenderItem, i: number, live: boolean, zone: 'process' | 'answer'): ReactNode => {
    if (item.kind === 'thinking-group') {
      return (
        <div key={`thinking-${i}`} className="mx-auto w-full max-w-2xl">
          <ThinkingGroup texts={item.texts} live={live} />
        </div>
      )
    }
    if (item.kind === 'tool-group') {
      return (
        <div key={`tools-${i}`} className="mx-auto w-full max-w-2xl">
          <ToolGroup steps={item.steps} live={live} />
        </div>
      )
    }
    if (zone === 'process' && item.block.phase !== 'final_answer') {
      return (
        <div key={`commentary-${i}`} className="mx-auto w-full max-w-2xl">
          <CommentaryText text={item.block.text} live={live} />
        </div>
      )
    }
    return (
      <div key={`text-${i}`} className="w-full text-[15px] leading-relaxed text-gray-900">
        {splitOnMarkers(item.block.text).map((part, j) => {
          if (part.type === 'md') return <Prose key={j} text={part.text} streaming={live} />
          if (part.type === 'chart') {
            const resolved = resolveChart(part.id)
            if (!resolved) {
              if (failedArtifacts[part.id] === 'chart') return <FailureBadge key={j}>Couldn't save chart</FailureBadge>
              return isStreaming ? null : <FailureBadge key={j}>Chart unavailable</FailureBadge>
            }
            rendered.charts.add(resolved.current.artifactId)
            return (
              <Suspense key={j} fallback={<ChartSkeleton />}>
                <ChartEmbed chart={resolved.current} versions={resolved.versions} />
              </Suspense>
            )
          }
          if (part.type === 'result') {
            const result = resultById.get(part.id)
            if (!result) {
              if (failedArtifacts[part.id] === 'result') return <FailureBadge key={j}>Couldn't save result</FailureBadge>
              return isStreaming ? null : <FailureBadge key={j}>Result unavailable</FailureBadge>
            }
            rendered.results.add(result.id)
            return <div key={j} className="mx-auto my-4 max-w-2xl"><ResultTile result={result} /></div>
          }
          if (part.type === 'spreadsheet') {
            const resolved = resolveSpreadsheet(part.id)
            if (!resolved) {
              if (failedArtifacts[part.id] === 'spreadsheet') return <FailureBadge key={j}>Couldn't save spreadsheet</FailureBadge>
              return isStreaming ? null : <FailureBadge key={j}>Spreadsheet unavailable</FailureBadge>
            }
            rendered.spreadsheets.add(resolved.current.artifactId)
            return (
              <SpreadsheetCard
                key={j}
                spreadsheet={resolved.current}
                versions={resolved.versions}
              />
            )
          }
          const label =
            part.kind === 'chart' ? "Couldn't save chart"
            : part.kind === 'spreadsheet' ? "Couldn't save spreadsheet"
            : "Couldn't save result"
          return <FailureBadge key={j}>{label}</FailureBadge>
        })}
      </div>
    )
  }

  // Lead answer = first text before any thinking/tools ("Yep, I'll check X…").
  // Process = work trace after that. Final answer = phase:final_answer deliverable.
  const { leadAnswerItems, processItems, finalAnswerItems, hasFinalAnswer } = splitTurnRenderItems(renderItems, isStreaming)
  const processToolCount = processItems.reduce((n, it) => n + (it.kind === 'tool-group' ? it.steps.length : 0), 0)
  const processLive = isStreaming && !hasFinalAnswer

  const renderAnswerItem = (item: RenderItem, globalIdx: number) =>
    renderItem(item, globalIdx, isStreaming && globalIdx === lastItemIndex, 'answer')

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-10 flex w-full flex-col text-[15px] leading-relaxed text-gray-900"
      data-testid="assistant-bubble"
      data-message-id={msg.id}
    >
      {leadAnswerItems.map((item, i) => renderAnswerItem(item, i))}
      {processItems.length > 0 && (
        <div className="mx-auto w-full max-w-2xl">
          <ProcessTrace toolCount={processToolCount} live={processLive}>
            {processItems.map((item, i) => renderItem(item, leadAnswerItems.length + i, processLive && i === processItems.length - 1, 'process'))}
          </ProcessTrace>
        </div>
      )}
      {finalAnswerItems.map((item, i) => {
        const globalIdx = leadAnswerItems.length + processItems.length + i
        return renderAnswerItem(item, globalIdx)
      })}
      {!isStreaming && artifacts.results
        .filter((r) => belongsToMessage(r.message_id, r.created_at) && !rendered.results.has(r.id))
        .map((r) => (
          <div key={`result-${r.id}`} className="mx-auto my-4 max-w-2xl">
            <ResultTile result={r} />
          </div>
        ))}
      {!isStreaming && artifacts.charts
        .filter((c) => belongsToMessage(c.messageId, c.createdAt) && !rendered.charts.has(c.artifactId))
        .filter((c, i, arr) => arr.findIndex((x) => x.artifactId === c.artifactId) === i)
        .map((c) => (
          <Suspense key={`chart-${c.artifactId}`} fallback={<ChartSkeleton />}>
            <ChartEmbed
              chart={chartVersionsByArtifact.get(c.artifactId)?.at(-1) ?? c}
              versions={chartVersionsByArtifact.get(c.artifactId) ?? [c]}
            />
          </Suspense>
        ))}
      {!isStreaming && artifacts.spreadsheets
        .filter((s) => belongsToMessage(s.messageId, s.createdAt) && !rendered.spreadsheets.has(s.artifactId))
        .filter((s, i, arr) => arr.findIndex((x) => x.artifactId === s.artifactId) === i)
        .map((s) => {
          const versions = spreadsheetVersionsByArtifact.get(s.artifactId) ?? [s]
          return (
            <SpreadsheetCard
              key={`spreadsheet-${s.artifactId}`}
              spreadsheet={versions.at(-1) ?? s}
              versions={versions}
            />
          )
        })}
      {isStreaming && (
        <div className="mx-auto w-full max-w-2xl">
          <ActivityIndicator blocks={aiBlocks} />
        </div>
      )}
      {!isStreaming && msg.interrupted && (
        <div className="mx-auto mt-1 w-full max-w-2xl">
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
            <span className="inline-block h-2 w-2 rounded-sm bg-gray-300" />
            Stopped
          </span>
        </div>
      )}
      {!isStreaming && hasContent && (
        <div className="mx-auto mt-2 flex w-full max-w-2xl items-center text-gray-400">
          <button
            onClick={handleCopy}
            className="rounded p-1.5 transition-colors hover:bg-gray-200/50 hover:text-gray-600"
            title={copied ? 'Copied!' : 'Copy'}
          >
            {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
          </button>
        </div>
      )}
    </motion.div>
  )
}

function MdImage(props: ComponentPropsWithoutRef<'img'>) {
  return <img {...props} />
}

const mdComponents = {
  h1: (props: ComponentPropsWithoutRef<'h1'>) => <h1 className="mt-5 mb-2 text-xl font-bold text-gray-900" {...props} />,
  h2: (props: ComponentPropsWithoutRef<'h2'>) => <h2 className="mt-4 mb-2 text-lg font-bold text-gray-900" {...props} />,
  h3: (props: ComponentPropsWithoutRef<'h3'>) => <h3 className="mt-3 mb-1.5 text-base font-semibold text-gray-900" {...props} />,
  p: (props: ComponentPropsWithoutRef<'p'>) => <p className="mb-3 last:mb-0" {...props} />,
  ul: (props: ComponentPropsWithoutRef<'ul'>) => <ul className="mb-3 list-disc space-y-1 pl-5" {...props} />,
  ol: (props: ComponentPropsWithoutRef<'ol'>) => <ol className="mb-3 list-decimal space-y-1 pl-5" {...props} />,
  li: (props: ComponentPropsWithoutRef<'li'>) => <li className="text-gray-800" {...props} />,
  strong: (props: ComponentPropsWithoutRef<'strong'>) => <strong className="font-semibold text-gray-900" {...props} />,
  a: (props: ComponentPropsWithoutRef<'a'>) => <a className="text-slate-700 underline hover:text-slate-900" {...props} />,
  code: ({ children, className, ...props }: ComponentPropsWithoutRef<'code'>) => {
    const isInline = !className
    return isInline
      ? <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[13px] text-gray-800" {...props}>{children}</code>
      : <code className={`mb-3 block overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-[13px] text-gray-800 ${className ?? ''}`} {...props}>{children}</code>
  },
  pre: (props: ComponentPropsWithoutRef<'pre'>) => <pre className="mb-3" {...props} />,
  table: (props: ComponentPropsWithoutRef<'table'>) => (
    <div className="mb-3 overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full border-collapse text-xs" {...props} />
    </div>
  ),
  thead: (props: ComponentPropsWithoutRef<'thead'>) => <thead className="border-b border-slate-200 bg-slate-100 text-slate-700" {...props} />,
  th: (props: ComponentPropsWithoutRef<'th'>) => <th className="px-3 py-2 text-left text-[11px] font-semibold" {...props} />,
  td: (props: ComponentPropsWithoutRef<'td'>) => <td className="border-t border-gray-100 px-3 py-2 font-mono text-gray-700" {...props} />,
  tr: (props: ComponentPropsWithoutRef<'tr'>) => <tr className="even:bg-gray-50/50" {...props} />,
  blockquote: (props: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="mb-3 border-l-2 border-gray-300 pl-3 italic text-gray-600" {...props} />
  ),
  hr: (props: ComponentPropsWithoutRef<'hr'>) => <hr className="my-4 border-gray-200" {...props} />,
  img: MdImage,
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {content}
    </ReactMarkdown>
  )
}

const thinkingMdComponents = {
  p: (props: ComponentPropsWithoutRef<'p'>) => <p className="mb-1.5 last:mb-0" {...props} />,
  ul: (props: ComponentPropsWithoutRef<'ul'>) => <ul className="mb-1.5 list-disc space-y-0.5 pl-4" {...props} />,
  ol: (props: ComponentPropsWithoutRef<'ol'>) => <ol className="mb-1.5 list-decimal space-y-0.5 pl-4" {...props} />,
  strong: (props: ComponentPropsWithoutRef<'strong'>) => <strong className="font-semibold" {...props} />,
  code: ({ children, className, ...props }: ComponentPropsWithoutRef<'code'>) => {
    const isInline = !className
    return isInline
      ? <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-600" {...props}>{children}</code>
      : <code className={`mb-1.5 block overflow-x-auto rounded bg-gray-100 p-2 font-mono text-[11px] text-gray-600 ${className ?? ''}`} {...props}>{children}</code>
  },
  a: (props: ComponentPropsWithoutRef<'a'>) => <a className="text-gray-500 underline hover:text-gray-700" {...props} />,
}

function CommentaryText({ text, live }: { text: string; live?: boolean }) {
  if (!text) return null
  return (
    <div className="my-1.5 border-l-2 border-gray-300 pl-3.5 text-[14px] leading-relaxed text-gray-800">
      <div className="whitespace-pre-wrap break-words">
        {text}
        {live && (
          <span className="ml-px inline-block h-[1.05em] w-[2px] translate-y-[2px] animate-pulse bg-indigo-400" aria-hidden />
        )}
      </div>
    </div>
  )
}

function ThinkingGroup({ texts, live }: { texts: string[]; live?: boolean }) {
  const [expanded, setExpanded] = useState(!!live)
  const userToggled = useRef(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!userToggled.current) setExpanded(!!live)
  }, [live])
  useEffect(() => {
    if (live && expanded && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [texts, live, expanded])
  return (
    <div className="my-1.5">
      <button
        onClick={() => { userToggled.current = true; setExpanded((v) => !v) }}
        className={`flex items-center gap-1.5 py-0.5 text-xs transition-colors hover:text-gray-600 ${live ? 'text-indigo-500' : 'text-gray-400'}`}
      >
        <ChevronRight size={11} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <Brain size={11} className={live ? 'animate-pulse' : ''} />
        <span>{live ? 'Thinking…' : 'Thought process'}</span>
      </button>
      {expanded && (
        <div ref={bodyRef} className="ml-5 mt-1 max-h-60 overflow-y-auto border-l-2 border-gray-200 pl-3 text-xs text-gray-500">
          {live ? (
            <div className="whitespace-pre-wrap break-words text-xs text-gray-500">
              {texts.join('')}
              <span className="ml-px inline-block h-[1em] w-[2px] translate-y-[1px] animate-pulse bg-indigo-400" aria-hidden />
            </div>
          ) : (
            texts.map((t, i) => (
              <div key={i} className={i > 0 ? 'mt-2' : ''}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={thinkingMdComponents}>{t}</ReactMarkdown>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function formatToolInput(input: Record<string, unknown> | null | undefined): string {
  if (!input || Object.keys(input).length === 0) return ''
  if (typeof input.command === 'string') return input.command
  try {
    return JSON.stringify(input, null, 2)
  } catch {
    return String(input)
  }
}

function truncateText(text: string, max: number): string {
  if (text.length <= max) return text
  return `${text.slice(0, max)}\n… (${text.length - max} more chars)`
}

function ToolStepRow({ step, live }: { step: ToolStep; live?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const running = !step.done && !!live
  const inputText = formatToolInput(step.input)
  const resultText = step.result?.trim() ?? ''
  const hasDetail = inputText.length > 0 || resultText.length > 0
  return (
    <div className="text-xs">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`flex w-full items-center gap-1.5 py-0.5 text-left transition-colors ${hasDetail ? 'hover:text-gray-700' : 'cursor-default'} ${step.isError ? 'text-red-500' : running ? 'text-indigo-500' : 'text-gray-500'}`}
      >
        {hasDetail
          ? <ChevronRight size={11} className={`shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          : <span className="w-[11px] shrink-0" />}
        {running
          ? <Loader2 size={11} className="shrink-0 animate-spin text-indigo-400" />
          : step.isError
            ? <AlertTriangle size={11} className="shrink-0 text-red-400" />
            : <Wrench size={11} className="shrink-0 text-gray-400" />}
        <span className="truncate">{toolLabel(step.name, step.input)}{running ? '…' : ''}</span>
      </button>
      {expanded && hasDetail && (
        <div className="ml-5 mt-1 mb-1 space-y-1 border-l-2 border-gray-200 pl-3">
          {inputText.length > 0 && (
            <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono text-[11px] text-gray-500">{truncateText(inputText, 2000)}</pre>
          )}
          {resultText.length > 0 && (
            <pre className={`max-h-48 overflow-auto whitespace-pre-wrap rounded p-2 font-mono text-[11px] ${step.isError ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'}`}>{truncateText(resultText, 4000)}</pre>
          )}
        </div>
      )}
    </div>
  )
}

function ToolGroup({ steps, live }: { steps: ToolStep[]; live?: boolean }) {
  if (steps.length === 0) return null
  return (
    <div className="my-1.5 space-y-0.5">
      {steps.map((s, i) => (
        <ToolStepRow key={i} step={s} live={live && i === steps.length - 1} />
      ))}
    </div>
  )
}

// Wraps the pre-answer reasoning + tool chain. Expanded and live while the
// agent works; once the turn finishes it collapses into a single summary
// line (like ChatGPT/Claude) so only the final answer stays prominent.
function ProcessTrace({
  toolCount, live, children,
}: {
  toolCount: number
  live: boolean
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(live)
  const userToggled = useRef(false)
  useEffect(() => {
    if (!userToggled.current) setExpanded(live)
  }, [live])

  const summary = toolCount > 0
    ? `Worked through ${toolCount} step${toolCount === 1 ? '' : 's'}`
    : 'Thought process'

  if (live || expanded) {
    return (
      <div className="my-1.5">
        {!live && (
          <button
            onClick={() => { userToggled.current = true; setExpanded(false) }}
            className="mb-1 flex items-center gap-1.5 py-0.5 text-xs text-gray-400 transition-colors hover:text-gray-600"
          >
            <ChevronDown size={11} />
            {toolCount > 0 ? <Wrench size={11} /> : <Brain size={11} />}
            <span>{summary}</span>
          </button>
        )}
        {children}
      </div>
    )
  }

  return (
    <button
      onClick={() => { userToggled.current = true; setExpanded(true) }}
      className="my-1.5 flex items-center gap-1.5 py-0.5 text-xs text-gray-400 transition-colors hover:text-gray-600"
    >
      <ChevronRight size={11} />
      {toolCount > 0 ? <Wrench size={11} /> : <Brain size={11} />}
      <span>{summary}</span>
    </button>
  )
}

const STATUS_CONFIG: Record<string, { color: string; label: string; pulse: boolean }> = {
  disconnected: { color: 'bg-red-400', label: 'Disconnected', pulse: false },
  connecting: { color: 'bg-yellow-400', label: 'Connecting...', pulse: true },
  provisioning: { color: 'bg-yellow-400', label: 'Setting up sandbox...', pulse: true },
  sandbox_ready: { color: 'bg-green-400', label: 'Ready', pulse: false },
  connected: { color: 'bg-green-400', label: 'Ready', pulse: false },
  thinking: { color: 'bg-slate-400', label: 'Thinking…', pulse: true },
}

function StatusDot({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.disconnected
  return (
    <div className="flex items-center gap-2">
      <span className="relative flex items-center">
        {cfg.pulse && (
          <span className={`absolute inline-flex h-2 w-2 rounded-full ${cfg.color} opacity-75 animate-ping`} />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${cfg.color}`} />
      </span>
      <span className="text-xs text-text-muted">{cfg.label}</span>
    </div>
  )
}

async function fileToAttachment(file: File): Promise<ImageAttachment | null> {
  if (!ALLOWED_MIMES.has(file.type)) return null
  if (file.size > MAX_ATTACHMENT_BYTES) return null
  const buf = await file.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
  return {
    mimeType: file.type,
    dataBase64: btoa(binary),
    previewUrl: URL.createObjectURL(file),
    name: file.name || 'image',
  }
}

function Composer({
  onSend,
  onStop,
  isStreaming,
}: {
  onSend: (t: string, attachments?: ImageAttachment[]) => void
  onStop: () => void
  isStreaming: boolean
}) {
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState<ImageAttachment[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const canSend = (text.trim().length > 0 || attachments.length > 0) && !isStreaming

  async function ingestFiles(files: FileList | File[]) {
    const slots = MAX_ATTACHMENTS - attachments.length
    if (slots <= 0) return
    const accepted: ImageAttachment[] = []
    for (const file of Array.from(files).slice(0, slots)) {
      const attachment = await fileToAttachment(file)
      if (attachment) accepted.push(attachment)
    }
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS))
    }
  }

  function removeAttachment(idx: number) {
    setAttachments((prev) => {
      const copy = prev.slice()
      const [removed] = copy.splice(idx, 1)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return copy
    })
  }

  const submit = () => {
    if (!canSend) return
    onSend(text.trim(), attachments)
    attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
    setAttachments([])
    setText('')
  }
  return (
    <div
      data-testid="prompt-input-container"
      className={`relative flex flex-col rounded-2xl border bg-white shadow-sm transition-all focus-within:border-gray-300 focus-within:shadow-md ${
        isDragging ? 'border-slate-400 ring-2 ring-slate-200' : 'border-gray-200'
      }`}
      onDragOver={(e) => {
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setIsDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragging(false)
        if (e.dataTransfer.files.length) void ingestFiles(e.dataTransfer.files)
      }}
    >
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {attachments.map((a, i) => (
            <div
              key={i}
              data-testid="attached-image-chip"
              className="group relative h-14 w-14 overflow-hidden rounded-md border border-gray-200 bg-gray-50"
              title={a.name}
            >
              <img src={a.previewUrl} alt={a.name} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeAttachment(i)}
                data-testid="attached-image-remove"
                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                aria-label={`Remove ${a.name}`}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
        data-testid="prompt-input"
        onPaste={(e) => {
          const files: File[] = []
          for (const item of Array.from(e.clipboardData.items ?? [])) {
            if (item.kind === 'file') {
              const file = item.getAsFile()
              if (file) files.push(file)
            }
          }
          if (files.length > 0) {
            e.preventDefault()
            void ingestFiles(files)
          }
        }}
        rows={1}
        placeholder="Ask about your data..."
        className="max-h-40 min-h-[56px] w-full resize-none bg-transparent px-5 py-4 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none"
      />
      <div className="flex items-center justify-between px-3 pb-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={attachments.length >= MAX_ATTACHMENTS}
          className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Attach image"
          title="Attach image"
        >
          <Paperclip size={16} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          data-testid="attach-file-input"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void ingestFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          onClick={isStreaming ? onStop : submit}
          disabled={!isStreaming && !canSend}
          className={`rounded-full p-1.5 transition-colors ${
            isStreaming
              ? 'bg-gray-900 text-white hover:bg-red-600'
              : canSend
                ? 'bg-gray-900 text-white hover:bg-black'
                : 'bg-gray-100 text-gray-400'
          }`}
        >
          {isStreaming ? <Square size={14} fill="currentColor" /> : <ArrowUp size={18} />}
        </button>
      </div>
    </div>
  )
}
